import "../styles/Sidebar.css";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaTachometerAlt,
  FaChartBar,
  FaCreditCard,
  FaUsers,
  FaCogs,
  FaBuilding,
  FaStore,
  FaUserTie,
  FaChartPie,
  FaLifeRing,
  FaSignOutAlt,
  FaTimes,
} from "react-icons/fa";
import qrivaLogo from "../resources/qr-icon-white.png";
import { useAuth } from "../context/AuthContext";
import { clearUserData, getUserData } from "../utils/authStorage";

const normalizeRoles = (value) => {
  if (!Array.isArray(value)) return [];
  return value.flat().filter(Boolean);
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const userData = getUserData();
  const user = userData?.user || userData || {}; // handle both { user: {...} } and {...}
  const userType = String(user?.user_type || user?.type || "").toLowerCase();
  const roles = normalizeRoles(user?.roles);

  const [logoutOpen, setLogoutOpen] = useState(false);

  const handleLogoutClick = () => setLogoutOpen(true);
  const handleCloseLogout = () => setLogoutOpen(false);
  const handleConfirmLogout = () => {
    localStorage.removeItem("token");
    clearUserData();
    logout();
    setLogoutOpen(false);
    navigate("/");
  };

  /**
   * Determine what to show based on user type and roles
   */
  const getVisibleMenuItems = () => {
    // Admin â€” show all
    if (userType === "admin") { //super admin usertype
      return ["dashboard", "transactions", "organizations", "analytics", "users", "settings"];
    }
    if (userType === "org") { // organization admin usertype
      return ["dashboard", "agents", "merchants", "users", "transactions", "reporting"];
    }
    // Subordinate â€” conditional based on roles
    if (userType === "subordinate") {
      // view-only => all access
      if (roles.includes("view-only")) {
        return ["dashboard", "reporting", "agents", "merchants", "terminals"];
      }

      // merchant_onboarding only
      if (roles.length === 1 && roles.includes("merchant_onboarding")) {
        return ["dashboard", "reporting", "unbind", "merchants"];
      }

      // terminal_creation only
      if (roles.length === 1 && roles.includes("agent_onboarding")) {
        return ["dashboard", "agents"];
      }
    }

    // default (no access)
    return [];
  };

  const visibleItems = getVisibleMenuItems();
  const usersLabel = userType === "admin" ? "Users" : "User Management";

  const menuItems = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <FaTachometerAlt />,
      path: "/dashboard",
    },
    {
      key: "transactions",
      label: "Transactions",
      icon: <FaCreditCard />,
      path: "/dashboard/transactions",
    },
    {
      key: "organizations",
      label: "Organizations",
      icon: <FaBuilding />,
      path: "/dashboard/organizationlist",
    },
    {
      key: "agents",
      label: "Agents",
      icon: <FaUserTie />,
      path: "/dashboard/agentslist",
    },
    {
      key: "merchants",
      label: "Merchants",
      icon: <FaStore />,
      path: "/dashboard/merchantsdetails",
    },
    {
      key: "analytics",
      label: "Analytics",
      icon: <FaChartBar />,
      path: "/dashboard/analytics",
    },
    {
      key: "users",
      label: usersLabel,
      icon: <FaUsers />,
      path: "/dashboard/userslist",
    },
    {
      key: "reporting",
      label: "Reporting",
      icon: <FaChartPie />,
      path: "/dashboard/analytics",
    },
  ];

  const bottomItems = [
    {
      key: "settings",
      label: "Settings",
      icon: <FaCogs />,
      path: "/dashboard/settings",
    },
    {
      key: "support",
      label: "Support",
      icon: <FaLifeRing />,
      path: "/dashboard/support",
    },
    {
      key: "logout",
      label: "Logout",
      icon: <FaSignOutAlt />,
      action: handleLogoutClick,
    },
  ];

  const consoleLabel =
    userType === "org"
      ? "ORGANIZATION ADMIN CONSOLE"
      : userType === "admin"
      ? "SUPER ADMIN CONSOLE"
      : "Enterprise Suite";

  const primaryBtnLabel = userType === "admin" ? "+ New Report" : "+ New Transaction";

  return (
    <div className="sidebar">
      <div className="sidebar-top">
        <div className="sidebar-brand" onClick={() => navigate("/dashboard")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") navigate("/dashboard");
          }}
        >
          <div className="sidebar-logo">
            <img src={qrivaLogo} alt="QRiva" />
          </div>
          <div className="sidebar-brand__meta">
            <div className="sidebar-brand__title">QRiva</div>
            <div className="sidebar-brand__sub">{consoleLabel}</div>
          </div>
        </div>

        <ul>
          {menuItems
            .filter((item) => visibleItems.includes(item.key))
            .map((item) => (
              <li
                key={item.key}
                onClick={() => navigate(item.path)}
                className={
                  location.pathname === item.path ||
                  (item.key !== "dashboard" && location.pathname.startsWith(item.path + "/"))
                    ? "active"
                    : ""
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </li>
            ))}
        </ul>
      </div>

      <div className="sidebar-actions">
        {userType !== "admin" && (
          <button className="sidebar-primary-btn" type="button" onClick={() => navigate("/dashboard/transactions")}
          >
            {primaryBtnLabel}
          </button>
        )}

        <ul className="sidebar-bottom-menu">
          {bottomItems.map((item) => (
            <li
              key={item.key}
              onClick={() => (item.action ? item.action() : navigate(item.path))}
              className={location.pathname === item.path ? "active" : ""}
            >
              {item.icon}
              <span>{item.label}</span>
            </li>
          ))}
        </ul>

        {/* Logout Confirmation Modal */}
        {logoutOpen &&
          createPortal(
            <div className="sb-logout-overlay">
              <div
                className="sb-logout-scrim"
                onClick={handleCloseLogout}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleCloseLogout()}
              />
              <div className="sb-logout-dialog">
                <div className="sb-logout-dialog__header">
                  <h3 className="sb-logout-dialog__title">Logout</h3>
                  <button
                    className="sb-logout-dialog__close"
                    onClick={handleCloseLogout}
                    type="button"
                    aria-label="Close"
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="sb-logout-dialog__body">
                  <p className="sb-logout-dialog__text">
                    Are you sure you want to logout?
                  </p>
                  <p className="sb-logout-dialog__subtext">
                    You will be redirected to the login page.
                  </p>
                </div>
                <div className="sb-logout-dialog__footer">
                  <button
                    className="sb-logout-dialog__cancel"
                    type="button"
                    onClick={handleCloseLogout}
                  >
                    Cancel
                  </button>
                  <button
                    className="sb-logout-dialog__confirm"
                    type="button"
                    onClick={handleConfirmLogout}
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

export default Sidebar;
